import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { AlertTriangle, Check } from 'lucide-react';

import { cn } from '@src/components/ui';

/** Shared transient-toast primitive. One queue implementation backs both the
 * app-wide GlobalToaster (#162) and the Listings copy/mark-listed feedback
 * (#365 dedup) — previously two parallel copies.
 *
 * `useToastQueue(duration)` owns the toast list and auto-dismisses each entry
 * after `duration` ms. `push(message, variant)` appends one. `<ToastStack>`
 * renders the list; callers control z-index and (to stay pixel-identical with
 * their prior bespoke markup) whether a danger toast shows the warning icon. */
function useToastQueue(duration) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(0);
  // Track pending auto-dismiss timers so they can be cleared on unmount — an
  // in-flight timer must never fire `setToasts` on an unmounted queue.
  const timers = useRef([]);

  const push = useCallback((message, variant = 'success') => {
    if (!message) {
      return;
    }
    const id = nextId.current;
    nextId.current += 1;
    setToasts((previous) => [...previous, { id, message, variant }]);
    const timer = setTimeout(() => {
      setToasts((previous) => previous.filter((toast) => toast.id !== id));
      timers.current = timers.current.filter((entry) => entry !== timer);
    }, duration);
    timers.current.push(timer);
  }, [duration]);

  // Clear every pending timer when the host unmounts.
  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  return { toasts, push };
}


/** Renders a stacked, bottom-centred list of toasts. `zIndexClassName` lets the
 * global surface sit above page-local ones (z-70 vs z-60), matching the prior
 * separate components. `dangerIcon` (default true) renders the AlertTriangle on
 * danger toasts; Listings opts out to preserve its icon-less danger styling. */
function ToastStack({ toasts, zIndexClassName = 'z-[70]', dangerIcon = true }) {
  if (!toasts.length) {
    return null;
  }
  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-5 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2',
        zIndexClassName,
      )}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'flex items-center gap-2 rounded-lg border border-hairline-strong bg-surface-1 px-3.5 py-2.5',
            'text-table font-medium text-ink shadow-lg',
            toast.variant === 'danger' && 'border-danger/30 text-danger',
          )}
        >
          {toast.variant === 'danger'
            ? (dangerIcon ? <AlertTriangle className="size-3.5 text-danger" /> : null)
            : <Check className="size-3.5 text-success" strokeWidth={3} />}

          {toast.message}
        </div>
      ))}
    </div>
  );
}

ToastStack.displayName = 'ToastStack';


/** Page-level toast adapter (audit d31ac04b). A thin over the queue: 2600ms
 * duration + icon-less danger styling, matching the original Listings toaster.
 * Previously defined under `pages/Listings/` yet imported by ~20 files across
 * nearly every page — moved here to its layout home; `pages/Listings/useToasts`
 * re-exports it so the existing import sites are unchanged. */
function useToasts() {
  return useToastQueue(2600);
}


function Toaster({ toasts }) {
  return <ToastStack zIndexClassName="z-[60]" toasts={toasts} dangerIcon={false} />;
}

Toaster.displayName = 'Toaster';


export {
  Toaster, ToastStack, useToastQueue, useToasts,
};
